// Module 10 — sim-clock → calendar/sun mapping (Batch C-2). The simulator's authoritative time is
// (season, hour) in LOCAL Toronto time (worker-state rule: one clock in the worker). Cesium's clock +
// SunLight are physical/UTC, so to make the sunlight ANGLE track the sim we map season → a
// representative day-of-year, combine it with the display year + the sim hour, and convert local→UTC.
// Pure + deterministic (NO Date / Date.now()) so it's unit-testable and replay-safe. The adapter feeds
// the UTC fields to GregorianDate→JulianDate and writes viewer.clock.currentTime; the LOCAL fields are
// what the date UI shows.
import type { Season } from "../domain/index";

// Representative day per season (Northern-hemisphere solstices/equinoxes). The sun's DECLINATION is
// what swings the lighting angle across seasons; these dates place it at the seasonal extremes/midpoints
// so "winter" visibly sits low and "summer" high at the same hour. (Toronto ≈ 43.7°N.)
const SEASON_DATE: Record<Season, { month: number; day: number }> = {
  winter: { month: 12, day: 21 }, // winter solstice — lowest midday sun
  spring: { month: 3, day: 20 }, // vernal equinox
  summer: { month: 6, day: 21 }, // summer solstice — highest midday sun
  fall: { month: 9, day: 22 }, // autumnal equinox
};

// Toronto observes EST (UTC−5) in winter, EDT (UTC−4) spring–fall. Each representative date falls in
// the matching DST regime (Dec 21 = EST; Mar 20 / Jun 21 / Sep 22 = EDT), so a per-season offset is
// exact for these anchors. Approximate (a true tz database is overkill for a sun ANGLE).
function utcOffsetHours(season: Season): number {
  return season === "winter" ? -5 : -4;
}

export interface SunDate {
  // LOCAL Toronto calendar — what the date UI shows.
  year: number;
  month: number; // 1-based
  day: number;
  hour: number; // 0..23, sim hour
  season: Season;
  // UTC calendar — fed straight to GregorianDate(year,month,day,hour) → JulianDate for clock.currentTime.
  utcYear: number;
  utcMonth: number; // 1-based
  utcDay: number;
  utcHour: number; // 0..23
}

// Map the sim's (season, hour) + a chosen display year to the calendar instant that drives the sun.
export function sunDate(season: Season, hour: number, year: number): SunDate {
  const { month, day } = SEASON_DATE[season];
  const h = ((Math.round(hour) % 24) + 24) % 24; // clamp/normalize the sim hour to 0..23
  let utcHour = h - utcOffsetHours(season); // local→UTC (offset is negative, so this ADDS hours)
  let dayShift = 0;
  if (utcHour >= 24) {
    utcHour -= 24;
    dayShift = 1;
  } else if (utcHour < 0) {
    utcHour += 24;
    dayShift = -1;
  }
  // The anchor days are mid-month (20/21/22), so a ±1-day UTC shift never crosses a month boundary —
  // utcDay stays a valid day-of-month without any Date rollover math.
  return {
    year, month, day, hour: h, season,
    utcYear: year, utcMonth: month, utcDay: day + dayShift, utcHour,
  };
}

// Pretty LOCAL date for the UI chip, e.g. "Jun 21, 2026 · 18:00 EDT".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatSunDate(d: SunDate): string {
  const hh = String(d.hour).padStart(2, "0");
  const tz = d.season === "winter" ? "EST" : "EDT";
  return `${MONTHS[d.month - 1]} ${d.day}, ${d.year} · ${hh}:00 ${tz}`;
}
